import React, { useCallback } from 'react';
import { Avatar, Box, ListItemButton, Typography } from '@mui/material';
import { formatCurrency } from '../../../shared/utils/currency';
import { maskAccountNumber, maskSortCode } from '../utils/bankDetailMasks';
import type { PaymentRowDto } from '../api/paymentsApi';

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 55%)`;
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export interface PaymentListItemProps {
  payment: PaymentRowDto;
  showBankDetails: boolean;
  onClick?: () => void;
  'data-testid'?: string;
}

export const PaymentListItem = React.memo(function PaymentListItem({
  payment,
  showBankDetails,
  onClick,
  'data-testid': dataTestId,
}: PaymentListItemProps) {
  const handleClick = useCallback(() => onClick?.(), [onClick]);
  const isPaid = payment.potStatus === 'Paid';
  const isPartiallyPaid = payment.potStatus === 'Partially Paid';

  return (
    <ListItemButton
      onClick={handleClick}
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Avatar with green ring for Paid, half-green ring for Partially Paid */}
      {isPartiallyPaid ? (
        <Box
          sx={{
            width: theme => theme.spacing(4.75),
            height: theme => theme.spacing(4.75),
            borderRadius: '50%',
            background: theme =>
              `conic-gradient(transparent 180deg, ${theme.palette.success.main} 180deg)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Avatar
            sx={{
              width: theme => theme.spacing(4),
              height: theme => theme.spacing(4),
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: stringToColor(payment.personName),
            }}
          >
            {getInitials(payment.personName)}
          </Avatar>
        </Box>
      ) : (
        <Avatar
          sx={{
            width: theme => theme.spacing(4),
            height: theme => theme.spacing(4),
            fontSize: '0.75rem',
            fontWeight: 600,
            bgcolor: stringToColor(payment.personName),
            flexShrink: 0,
            ...(isPaid && {
              outline: '2px solid',
              outlineColor: 'success.main',
              outlineOffset: 1,
            }),
          }}
        >
          {getInitials(payment.personName)}
        </Avatar>
      )}

      {/* Name + status text */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {payment.personName}
        </Typography>
        <Typography
          variant="caption"
          color={isPaid || isPartiallyPaid ? 'success.main' : 'text.secondary'}
        >
          {payment.potStatus}
        </Typography>
      </Box>

      {/* Account Number — always visible, masked/unmasked by toggle */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: 80,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {showBankDetails
          ? payment.accountNumber || '--'
          : maskAccountNumber(payment.accountNumber)}
      </Typography>

      {/* Sort Code — always visible, masked/unmasked by toggle */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: 70,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {showBankDetails
          ? payment.sortCode || '--'
          : maskSortCode(payment.sortCode)}
      </Typography>

      {/* Amount — always visible */}
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          width: 80,
          textAlign: 'right',
          flexShrink: 0,
        }}
      >
        {formatCurrency(payment.amountDue)}
      </Typography>
    </ListItemButton>
  );
});
